import "./style.css";
import {
    getSavedPosition,
    savePosition,
    getMapStyle,
    saveMapStyle,
    getSavedZoom,
    saveZoom,
} from "./src/states";

import { SERVER } from "./src/defaults";
import { Map, Overlay, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat, toLonLat } from "ol/proj";
import { BingMaps } from "ol/source";
import MousePosition from "ol/control/MousePosition";
import { createStringXY } from "ol/coordinate";
import { DEFAULT_POSITION, DEFAULT_ZOOM } from "./src/defaults";
import { addLayers, layers, layersWMS } from "./src/layers";
import {
    fgbBoundingBox,
    checkGeometry,
    emptyIfNull,
    emptyIfNull2,
    getCount,
} from "./src/helpers";
import { updateAllLayers } from "./src/update";
import { handleModal } from "./src/modal";
import { UI } from "./src/ui";
import { layerController } from "./src/layer-controller";

/**
 * Initializes map with given options and in given container.
 * @param {string} containerId
 * @param {{width: string, height: string, mapType: "gna" | "altre-banche-dati" | "iccd" | "concessione"}} options
 * @returns
 */
const initMap = (
    containerId,
    options = {
        width: "100%",
        height: "100vh",
        mapType: "gna",
    }
) => {
    const container = document.querySelector(`#${containerId}`);
    if (!container) {
        return;
    }

    const { width, height, mapType } = options;

    container.innerHTML = UI;

    const ct = document.getElementById(containerId);
    ct.style.width = width;
    ct.style.height = height;

    container.classList.add("gna-map-container");

    (async () => {
        try {
            const totalPoints = document.querySelector("#total-points");
            const totalLines = document.querySelector("#total-lines");
            const totalPolygons = document.querySelector("#total-polygons");
            const totalPointsTess =
                document.querySelector("#total-points-tess");
            const totalPointsCensimento = document.querySelector(
                "#total-points-censimento"
            );

            // totalPoints.innerHTML =

            totalPoints.innerHTML = await getCount("mosi_multipoint_pt");
            totalLines.innerHTML = await getCount("mosi_multilinea_ln");
            totalPolygons.innerHTML = await getCount("mosi_multipolygon_pl");
            totalPointsTess.innerHTML = await getCount("tess_pt");
            totalPointsCensimento.innerHTML = await getCount("Censimento_pt");
        } catch (error) {
            console.error("Error fetching counts:", error);
        }
    })();

    const pos = getSavedPosition();
    const zoom = getSavedZoom();

    const osmLayer = new TileLayer({
        source: new OSM(),
    });

    const sateliteLayer = new TileLayer({
        source: new BingMaps({
            key: "AgVdW6C-oTbvAyQNYffPvJPwp3M7JAFCVr_cQgB4qrDBcSFA2tq6cCh0u4vp7D1f",
            imagerySet: "Aerial",
        }),
    });
    const mapStyle = getMapStyle() == "OSM" ? osmLayer : sateliteLayer;

    const mousePositionControl = new MousePosition({
        coordinateFormat: createStringXY(6),
        projection: "EPSG:4326",
        target: document.querySelector("#gna-coords"),
    });

    const map = new Map({
        target: containerId,
        layers: [mapStyle],
        view: new View({
            center: fromLonLat(pos),
            zoom: zoom,
            minZoom: 5,
            maxZoom: 20,
        }),
        controls: [mousePositionControl],
    });

    addLayers(map);

    const popupElement = document.querySelector("#gna-popup");
    const homeBtn = document.querySelector("#gna-home-button");
    const zoomInBtn = document.querySelector("#gna-zoom-in-button");
    const zoomOutBtn = document.querySelector("#gna-zoom-out-button");
    const toggleMapBtn = document.querySelector("#gna-toggle-map-button");
    const closeModalBtn = document.querySelector("#gna-close-modal-button");
    const openSidebarBtn = document.querySelector("#gna-open-sidebar-button");
    const closeSidebarBtn = document.querySelector("#gna-close-sidebar-button");
    const locationBtn = document.querySelector("#gna-location-button");
    const sidebar = document.querySelector("#gna-sidebar");
    const modal = document.querySelector("#gna-modal");
    const modalDetails = document.querySelector("#gna-modal-details");
    const modalHeaderTitle = document.querySelector("#gna-modal-header-title");
    const modalContent = document.querySelector("#gna-modal-content");

    const toggleModal = () => {
        modal.classList.toggle("active");
    };

    const handleClickOutsideModal = (e) => {
        if (modal.classList.contains("active") && e.target === modal) {
            toggleModal();
        }
    };

    const toggleSidebar = () => {
        sidebar.classList.toggle("active");
    };

    const popup = new Overlay({
        element: popupElement,
        positioning: "bottom-center",
        stopEvent: false,
    });
    map.addOverlay(popup);
    const disposePopover = () => {
        popup.setPosition(undefined);
    };

    map.on("pointermove", (e) => {
        const pixel = map.getEventPixel(e.originalEvent);
        const hit = map.hasFeatureAtPixel(pixel);
        if (hit) {
            map.getTargetElement().style.cursor = "pointer";
        } else {
            map.getTargetElement().style.cursor = "";
        }

        const feature = map.forEachFeatureAtPixel(e.pixel, (feature) => {
            return feature;
        });
        disposePopover();

        if (!feature) {
            return;
        }
        const url = feature.get("url");
        const gid = feature.get("gid");
        const accc = feature.get("ACCC");
        const ogd = feature.get("OGD");
        const ogt = feature.get("OGT");
        const ogn = feature.get("OGN");
        const dtr = feature.get("DTR");
        const nome = feature.get("nome");
        const des = feature.get("descrizione") ?? feature.get("Descrizione");
        const ogg = feature.get("Oggetto");
        const tit = feature.get("Titolo");
        const regex = /js\/([^_]+)_pt/;
        const name = url.match(regex);
        const macrocategoria = feature.get("macrocategoria");
        const icon =
            SERVER +
            "images/MOSI_multipoint" +
            feature.get("id_macrocategoria") +
            ".svg";
        const geometryName = checkGeometry(url);

        popup.setPosition(e.coordinate);

        switch (layerController.currentMap) {
            case "gna":
                popupElement.innerHTML = `
                <h6 class="gna-name">Siti (${geometryName})</h6>
                ${emptyIfNull2(icon, macrocategoria)}
                ${emptyIfNull("GID", gid)}
                ${emptyIfNull("ACCC", accc)}
                ${emptyIfNull("OGD", ogd)}
                ${emptyIfNull("OGT", ogt)}
                ${emptyIfNull("OGN", ogn)}
                ${emptyIfNull("DTR", dtr)}`;
                break;
            case "altre-banche-dati":
                popupElement.innerHTML = `
                <h6 class="gna-name">${name[1]}</h6>
                ${emptyIfNull("Oggetto", ogg)}
                ${emptyIfNull("Titolo", tit)}
                ${emptyIfNull("Nome", nome)}
                ${emptyIfNull("Descrizione", des)}
                `;
                break;
            case "iccd":
                popupElement.innerHTML = `
                <h6 class="gna-name">Catalogo (ICCD)</h6>
                ${emptyIfNull("Nome", nome)}
                `;
                break;
            case "concessione":
                popupElement.innerHTML = `
                <h6 class="gna-name">Concessioni</h6>
                ${emptyIfNull("Nome", nome)}
                ${emptyIfNull("Descrizione", des)}
                `;
                break;
            default:
                popupElement.innerHTML = `
                <h6 class="gna-name">Unknown layer</h6>
                `;
        }

        console.log(feature);
    });

    map.on("click", (e) => handleModal(e, map, toggleModal));

    map.on("movestart", disposePopover);

    let debounce;

    map.on("moveend", () => {
        const view = map.getView();
        const center = toLonLat(view.getCenter());
        const zoom = view.getZoom();
        savePosition(center);
        saveZoom(zoom);

        // layersWMS.ICCD.getSource().refresh();

        if (debounce) {
            clearTimeout(debounce);
        }

        debounce = setTimeout(() => {
            updateAllLayers(fgbBoundingBox(map));
        }, 2000);
    });

    const radios = document.querySelectorAll(".gna-map-type-radio");

    radios.forEach((r) =>
        r.addEventListener("click", () => {
            setTimeout(() => updateAllLayers(fgbBoundingBox(map)), 1000);
        })
    );

    homeBtn.addEventListener("click", () => {
        const view = map.getView();
        view.animate({
            center: fromLonLat(DEFAULT_POSITION),
            zoom: DEFAULT_ZOOM,
            duration: 1000,
        });
        savePosition(DEFAULT_POSITION);
    });
    zoomInBtn.addEventListener("click", () => {
        const view = map.getView();
        const zoom = view.getZoom();
        if (zoom < view.getMaxZoom()) {
            view.animate({
                zoom: zoom + 1,
                duration: 500,
            });
        }
    });
    zoomOutBtn.addEventListener("click", () => {
        const view = map.getView();
        const zoom = view.getZoom();
        if (zoom > view.getMinZoom()) {
            view.animate({
                zoom: zoom - 1,
                duration: 500,
            });
        }
    });
    toggleMapBtn.addEventListener("click", () => {
        if (map.getLayers().getArray().includes(osmLayer)) {
            map.removeLayer(osmLayer);
            map.getLayers().insertAt(0, sateliteLayer);
            saveMapStyle("SAT");
        } else {
            map.removeLayer(sateliteLayer);
            map.getLayers().insertAt(0, osmLayer);
            saveMapStyle("OSM");
        }
    });
    locationBtn.addEventListener("click", () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { longitude, latitude } = position.coords;
                    const view = map.getView();
                    view.animate({
                        center: fromLonLat([longitude, latitude]),
                        zoom: 16,
                        duration: 1000,
                    });
                    savePosition([longitude, latitude]);
                    saveZoom(16);
                },
                (error) => {
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            alert("Permission to access location was denied.");
                            break;
                        case error.POSITION_UNAVAILABLE:
                            alert("Location information is unavailable.");
                            break;
                        case error.TIMEOUT:
                            alert(
                                "The request to get user location timed out."
                            );
                            break;
                        case error.UNKNOWN_ERROR:
                        default:
                            alert("An unknown error occurred.");
                            break;
                    }
                }
            );
        } else {
            alert("Geolocation is not supported by this browser.");
        }
    });
    openSidebarBtn.addEventListener("click", toggleSidebar);
    closeSidebarBtn.addEventListener("click", toggleSidebar);
    closeModalBtn.addEventListener("click", toggleModal);

    const checkBoxPunti = document.querySelector("#gna-dati-checkbox-punti");
    const checkBoxLinee = document.querySelector("#gna-dati-checkbox-linee");
    const checkBoxPoligoni = document.querySelector(
        "#gna-dati-checkbox-poligoni"
    );
    const checkBoxProgetti = document.querySelector(
        "#gna-dati-checkbox-progetti"
    );
    const checkBoxRLinee = document.querySelector(
        "#gna-dati-checkbox-ricostruti-linee"
    );
    const checkBoxRPoligoni = document.querySelector(
        "#gna-dati-checkbox-ricostruti-poligoni"
    );

    checkBoxPunti.addEventListener("click", (e) => {
        layers.GNA.punti.setVisible(e.target.checked);
        layersWMS.GNA.multipoint.setVisible(e.target.checked);
    });

    checkBoxLinee.addEventListener("click", (e) => {
        layers.GNA.linee.setVisible(e.target.checked);
        layersWMS.GNA.multiline.setVisible(e.target.checked);
    });

    checkBoxPoligoni.addEventListener("click", (e) => {
        layers.GNA.poligoni2.setVisible(e.target.checked);
        layersWMS.GNA.multipolygon.setVisible(e.target.checked);
    });

    checkBoxProgetti.addEventListener("click", (e) => {
        layers.GNA.progetti.setVisible(e.target.checked);
    });

    checkBoxRLinee.addEventListener("click", (e) => {
        layers.GNA.ricostruitiLinee.setVisible(e.target.checked);
    });

    checkBoxRPoligoni.addEventListener("click", (e) => {
        layers.GNA.ricostruitiPoligoni.setVisible(e.target.checked);
    });
    document.addEventListener("click", handleClickOutsideModal);

    layerController.init(mapType);

    return map;
};

//temporary - wip
export let globalMap;

document.addEventListener("DOMContentLoaded", () => {
    globalMap = initMap("map");
});

export { initMap };
